import type { TelemetryEvent } from "@genz/platform";

type SendResult = { accepted: number; rejected: number };

/** The two members of the cloud client the logger needs. */
export interface TelemetryClientLike {
  /** Synchronous on purpose: a Promise here would read as "signed in" forever. */
  getTokens(): object | null | undefined;
  sendEvents(events: TelemetryEvent[]): Promise<SendResult>;
}

export interface LateBoundTelemetryClient {
  /** Bind the real client. Call exactly once, right after `createAppServices` resolves. */
  bind(client: TelemetryClientLike): void;
  /** `false` until a client is bound AND holds tokens. Never throws. */
  isAuthenticated(): boolean;
  /** Rejects, naming the cause, when called before `bind`. */
  sendEvents(events: TelemetryEvent[]): Promise<SendResult>;
}

/**
 * The ClientLogger is built BEFORE `createAppServices` (its `observeRequest` is wired into the
 * JsonClient at construction), yet it needs that client to answer "are we signed in?" and to
 * send. Both entrypoints used to close over `const services`, declared after an `await` — any
 * flush during that await read it inside its temporal dead zone and threw as an unhandled
 * rejection. This handle is safe to call at any time: before `bind` there is truthfully no
 * client and no session, so the logger's flush waits and the buffer is kept.
 */
export function createLateBoundTelemetryClient(): LateBoundTelemetryClient {
  let client: TelemetryClientLike | undefined;
  return {
    bind(c) {
      if (client) throw new Error("telemetry client already bound");
      client = c;
    },
    isAuthenticated: () => (client ? !!client.getTokens() : false),
    sendEvents: (events) =>
      client
        ? client.sendEvents(events)
        : Promise.reject(new Error("telemetry send attempted before the cloud client was bound")),
  };
}
